import React, { useState, useMemo } from 'react';
import './ZoneWeeklyView.css';

const ZoneWeeklyView = ({
  zoneWiseData,
  trainerTasks,
  trainers,
  weekStartDate,
  onWeekChange,
  onTaskCreate,
  onTaskUpdate,
  onTaskDelete,
  onFacilitatorChange,
  isScheduler,
  savingSession,
  includeCompleted = false,
  includeUpcoming = false,
  onStatusFilterChange
}) => {
  const [taskFormDate, setTaskFormDate] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({ trainerId: '', description: '', duration: 'full_day' });

  // Get trainer initials from name
  const getInitials = (name) => {
    if (!name) return '';
    return name.split(' ').map(w => w[0]).join('').toUpperCase();
  };

  // Compute the 6 days of the week (Mon-Sat)
  const weekDays = useMemo(() => {
    const days = [];
    const parts = weekStartDate.split('-');
    const start = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    for (let i = 0; i < 6; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      const dateStr = d.getFullYear() + '-' +
        String(d.getMonth() + 1).padStart(2, '0') + '-' +
        String(d.getDate()).padStart(2, '0');
      days.push({
        name: dayNames[i],
        date: d,
        dateStr,
        label: d.toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })
      });
    }
    return days;
  }, [weekStartDate]);

  // Check if a Saturday is the 2nd or 4th Saturday
  const isSecondOrFourthSaturday = (date) => {
    if (date.getDay() !== 6) return false;
    const satNum = Math.ceil(date.getDate() / 7);
    return satNum === 2 || satNum === 4;
  };

  // Saturday of this week
  const saturdayDate = weekDays[5]?.date;
  const isSaturdayOff = saturdayDate && isSecondOrFourthSaturday(saturdayDate);

  // Flatten zone data into ordered batch columns
  const batchColumns = useMemo(() => {
    if (!zoneWiseData || zoneWiseData.length === 0) return [];

    const columns = [];
    zoneWiseData.forEach(({ zone, centers }) => {
      centers.forEach(({ center, batches }) => {
        batches.forEach(({ batch, sessions }) => {
          // Build a map of dateStr -> sessions for quick lookup
          const sessionsByDate = {};
          sessions.forEach(s => {
            const dateKey = s.session_date ? String(s.session_date).split('T')[0] : null;
            if (dateKey) {
              if (!sessionsByDate[dateKey]) sessionsByDate[dateKey] = [];
              sessionsByDate[dateKey].push(s);
            }
          });

          columns.push({
            zone,
            center,
            batch,
            sessions,
            sessionsByDate
          });
        });
      });
    });
    return columns;
  }, [zoneWiseData]);

  // Group columns by zone and center for multi-level headers
  const headerGroups = useMemo(() => {
    const zones = [];
    let currentZone = null;
    let currentCenter = null;

    batchColumns.forEach((col, idx) => {
      if (!currentZone || currentZone.name !== col.zone) {
        currentZone = { name: col.zone, centers: [], startIdx: idx, count: 0 };
        currentCenter = null;
        zones.push(currentZone);
      }
      if (!currentCenter || currentCenter.id !== col.center.id) {
        currentCenter = { id: col.center.id, name: col.center.name, batches: [], startIdx: idx, count: 0 };
        currentZone.centers.push(currentCenter);
      }
      currentCenter.batches.push(col);
      currentCenter.count++;
      currentZone.count++;
    });

    return zones;
  }, [batchColumns]);

  // Get trainer tasks grouped by date
  const tasksByDate = useMemo(() => {
    const map = {};
    if (trainerTasks) {
      trainerTasks.forEach(task => {
        const dateKey = String(task.task_date).split('T')[0];
        if (!map[dateKey]) map[dateKey] = [];
        map[dateKey].push(task);
      });
    }
    return map;
  }, [trainerTasks]);

  // Compute WK/D display for a batch in the current week
  const getBatchWeekInfo = (col) => {
    const sessionsInWeek = [];
    weekDays.forEach(day => {
      const daySessions = col.sessionsByDate[day.dateStr];
      if (daySessions) sessionsInWeek.push(...daySessions);
    });
    if (sessionsInWeek.length === 0) {
      // Fallback: check all sessions for week_number range
      if (col.sessions.length > 0) {
        const weeks = [...new Set(col.sessions.map(s => s.week_number))].sort((a, b) => a - b);
        const days = [...new Set(col.sessions.map(s => s.day_number))].sort((a, b) => a - b);
        return `WK ${weeks[0]} D ${days[0]}-${days[days.length - 1]}`;
      }
      return '';
    }
    const weeks = [...new Set(sessionsInWeek.map(s => s.week_number))].sort((a, b) => a - b);
    const dayNums = [...new Set(sessionsInWeek.map(s => s.day_number))].sort((a, b) => a - b);
    if (weeks.length === 1) {
      return `WK ${weeks[0]} D ${dayNums[0]}-${dayNums[dayNums.length - 1]}`;
    }
    return `WK ${weeks[0]} D ${dayNums[0]}-WK ${weeks[weeks.length - 1]} D ${dayNums[dayNums.length - 1]}`;
  };

  // Navigate weeks
  const handlePrevWeek = () => {
    const parts = weekStartDate.split('-');
    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    d.setDate(d.getDate() - 7);
    onWeekChange(d.getFullYear() + '-' +
      String(d.getMonth() + 1).padStart(2, '0') + '-' +
      String(d.getDate()).padStart(2, '0'));
  };

  const handleNextWeek = () => {
    const parts = weekStartDate.split('-');
    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    d.setDate(d.getDate() + 7);
    onWeekChange(d.getFullYear() + '-' +
      String(d.getMonth() + 1).padStart(2, '0') + '-' +
      String(d.getDate()).padStart(2, '0'));
  };

  // Task form handlers
  const openTaskForm = (dateStr) => {
    setTaskFormDate(dateStr);
    setEditingTask(null);
    setTaskForm({ trainerId: '', description: '', duration: 'full_day' });
    setTaskFormError('');
  };

  const openEditTask = (task) => {
    setEditingTask(task);
    setTaskFormDate(String(task.task_date).split('T')[0]);
    setTaskForm({
      trainerId: task.trainerId || '',
      description: task.description || '',
      duration: task.duration || 'full_day'
    });
    setTaskFormError('');
  };

  const [taskFormError, setTaskFormError] = useState('');

  const handleTaskSubmit = async () => {
    if (!taskForm.trainerId && !taskForm.description.trim()) {
      setTaskFormError('Please select a facilitator and enter a description.');
      return;
    }
    if (!taskForm.trainerId) {
      setTaskFormError('Please select a facilitator.');
      return;
    }
    if (!taskForm.description.trim()) {
      setTaskFormError('Please enter a task description.');
      return;
    }
    setTaskFormError('');
    if (editingTask) {
      await onTaskUpdate(editingTask.id, { ...taskForm, task_date: taskFormDate });
    } else {
      await onTaskCreate({ ...taskForm, task_date: taskFormDate });
    }
    setTaskFormDate(null);
    setEditingTask(null);
    setTaskForm({ trainerId: '', description: '', duration: 'full_day' });
  };

  const cancelTaskForm = () => {
    setTaskFormDate(null);
    setEditingTask(null);
    setTaskFormError('');
  };

  // Format week range for header
  const weekRangeLabel = useMemo(() => {
    if (weekDays.length < 6) return '';
    const mon = weekDays[0].date;
    const sat = weekDays[5].date;
    const fmt = (d) => d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    return `${fmt(mon)} - ${fmt(sat)}`;
  }, [weekDays]);

  if (!zoneWiseData) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
        <span>Loading zone data...</span>
      </div>
    );
  }

  const hasData = zoneWiseData.length > 0 || (trainerTasks && trainerTasks.length > 0);

  return (
    <div className="zone-weekly-view">
      {/* Week Navigation */}
      <div className="zone-week-nav">
        <button className="week-nav-btn" onClick={handlePrevWeek}>&lt; Prev Week</button>
        <h3 className="week-nav-title">Schedule for week starting from {weekRangeLabel}</h3>
        <button className="week-nav-btn" onClick={handleNextWeek}>Next Week &gt;</button>
      </div>

      {/* Batch-status filter toggles */}
      {onStatusFilterChange && (
        <div className="zone-status-filters">
          <label className="zone-status-filter">
            <input
              type="checkbox"
              checked={includeCompleted}
              onChange={(e) => onStatusFilterChange({ includeCompleted: e.target.checked, includeUpcoming })}
            />
            <span>Show Completed batches</span>
          </label>
          <label className="zone-status-filter">
            <input
              type="checkbox"
              checked={includeUpcoming}
              onChange={(e) => onStatusFilterChange({ includeCompleted, includeUpcoming: e.target.checked })}
            />
            <span>Show Upcoming batches</span>
          </label>
        </div>
      )}

      {!hasData && (
        <div className="empty-table-message">
          <p>No schedule data available for this week. Select a different week, toggle batch filters, or upload schedules.</p>
        </div>
      )}

      {hasData && <>
      {/* Spreadsheet Table */}
      <div className="zone-weekly-table-wrapper">
        <table className="zone-weekly-table">
          <thead>
            {/* Row 1: Zone headers */}
            <tr className="zone-header-row">
              <th className="task-col-header" rowSpan="3"></th>
              {headerGroups.map(zoneGroup => (
                <th
                  key={zoneGroup.name}
                  colSpan={zoneGroup.count}
                  className="zone-header-cell"
                >
                  {zoneGroup.name}
                </th>
              ))}
            </tr>
            {/* Row 2: Center headers */}
            <tr className="center-header-row">
              {headerGroups.flatMap(zoneGroup =>
                zoneGroup.centers.map(centerGroup => (
                  <th
                    key={centerGroup.id}
                    colSpan={centerGroup.count}
                    className="center-header-cell"
                  >
                    {centerGroup.name}
                  </th>
                ))
              )}
            </tr>
            {/* Row 3: Batch info headers */}
            <tr className="batch-header-row">
              {batchColumns.map((col, idx) => {
                const isMorning = col.batch.batch_type !== 'afternoon';
                const timing = col.batch.batch_type === 'custom'
                  ? `${col.batch.start_time || ''} - ${col.batch.end_time || ''}`
                  : `${col.batch.start_time || (isMorning ? '10:00 AM' : '2:00 PM')} to ${col.batch.end_time || (isMorning ? '1:00 PM' : '5:00 PM')}`;
                const coordinator = col.batch.coordinator?.name || '';
                const weekInfo = getBatchWeekInfo(col);

                return (
                  <th
                    key={idx}
                    className={`batch-info-header ${isMorning ? 'morning-batch' : 'afternoon-batch'}`}
                  >
                    <div className="batch-info-name">{col.batch.batch_name}</div>
                    {weekInfo && <div className="batch-info-week">{weekInfo}</div>}
                    <div className="batch-info-timing">{timing}</div>
                    {coordinator && <div className="batch-info-coordinator">{coordinator}</div>}
                    {col.batch.strength && <div className="batch-info-strength">Str: {col.batch.strength}</div>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {weekDays.map((day) => {
              const dayTasks = tasksByDate[day.dateStr] || [];
              const isOff = day.name === 'Saturday' && isSaturdayOff;

              return (
                <tr key={day.dateStr} className={isOff ? 'saturday-off-row' : ''}>
                  {/* Left column: Day + Trainer Tasks */}
                  <td className="task-column">
                    <div className="day-info">
                      <span className="day-name">{day.name}</span>
                      <span className="day-date-label">
                        {day.date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </span>
                    </div>
                    {isOff ? (
                      <div className="saturday-off-label">
                        {Math.ceil(day.date.getDate() / 7) === 4 ? '4th' : '2nd'} Saturday Off
                      </div>
                    ) : (
                      <>
                        <div className="day-tasks">
                          {dayTasks.map(task => (
                            <div key={task.id} className="task-pill" onClick={() => isScheduler && openEditTask(task)}>
                              <span className="task-trainer">{getInitials(task.trainer?.name)}</span>
                              <span className="task-desc">- {task.description}</span>
                              {task.duration !== 'full_day' && (
                                <span className="task-duration">({task.duration === 'morning' ? 'AM' : 'PM'})</span>
                              )}
                              {isScheduler && (
                                <button
                                  className="task-delete-btn"
                                  onClick={(e) => { e.stopPropagation(); onTaskDelete(task.id); }}
                                  title="Delete task"
                                >x</button>
                              )}
                            </div>
                          ))}
                        </div>
                        {isScheduler && (
                          <>
                            {taskFormDate === day.dateStr ? (
                              <div className="task-form-inline">
                                <select
                                  value={taskForm.trainerId}
                                  onChange={(e) => setTaskForm(f => ({ ...f, trainerId: e.target.value }))}
                                  className="task-form-select"
                                >
                                  <option value="">Facilitator</option>
                                  {trainers.map(t => (
                                    <option key={t.id} value={t.id}>{t.name}</option>
                                  ))}
                                </select>
                                <input
                                  type="text"
                                  placeholder="Task description"
                                  value={taskForm.description}
                                  onChange={(e) => setTaskForm(f => ({ ...f, description: e.target.value }))}
                                  className="task-form-input"
                                />
                                <select
                                  value={taskForm.duration}
                                  onChange={(e) => setTaskForm(f => ({ ...f, duration: e.target.value }))}
                                  className="task-form-duration"
                                >
                                  <option value="full_day">Full Day</option>
                                  <option value="morning">Morning</option>
                                  <option value="afternoon">Afternoon</option>
                                </select>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  <button onClick={handleTaskSubmit} className="task-form-save">Save</button>
                                  <button onClick={cancelTaskForm} className="task-form-cancel">Cancel</button>
                                </div>
                                {taskFormError && <div className="task-form-error">{taskFormError}</div>}
                              </div>
                            ) : (
                              <button
                                className="add-task-btn"
                                onClick={() => openTaskForm(day.dateStr)}
                                title="Add facilitator task"
                              >+ Add Task</button>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </td>

                  {/* Batch columns */}
                  {batchColumns.map((col, colIdx) => {
                    if (isOff) {
                      return <td key={colIdx} className="saturday-off-cell"></td>;
                    }

                    const daySessions = col.sessionsByDate[day.dateStr] || [];
                    const isMorning = col.batch.batch_type !== 'afternoon';

                    if (daySessions.length === 0) {
                      return <td key={colIdx} className={`batch-cell empty-batch-cell ${isMorning ? 'morning-cell' : 'afternoon-cell'}`}></td>;
                    }

                    return (
                      <td key={colIdx} className={`batch-cell ${isMorning ? 'morning-cell' : 'afternoon-cell'}`}>
                        {daySessions.map((session, sIdx) => (
                          <div key={sIdx} className="session-entry">
                            <div className={`session-content-text ${isMorning ? '' : 'afternoon-text'}`}>
                              {session.session_content || '-'}
                            </div>
                            <div className="session-facilitator">
                              {isScheduler ? (
                                <>
                                  <select
                                    value={session.trainerId || ''}
                                    onChange={(e) => onFacilitatorChange(session.id, e.target.value)}
                                    disabled={savingSession === session.id}
                                    className="facilitator-mini-select"
                                  >
                                    <option value="">--</option>
                                    {trainers.map(t => (
                                      <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                  </select>
                                  <span className={`facilitator-print-text ${isMorning ? '' : 'afternoon-text'}`}>
                                    {session.trainer?.name || ''}
                                  </span>
                                </>
                              ) : (
                                <span className={`facilitator-initials ${isMorning ? '' : 'afternoon-text'}`}>
                                  {session.trainer?.name || ''}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Saturday off footer note */}
      {isSaturdayOff && (
        <div className="saturday-off-footer">
          {Math.ceil(saturdayDate.getDate() / 7) === 4 ? '4th' : '2nd'} Saturday off
        </div>
      )}
      </>}
    </div>
  );
};

export default ZoneWeeklyView;
